/**
 * BlueDotTransition - Login Intro Animation (Responsive)
 *
 * Minimal, elegant intro:
 * 1. Blue dot covers entire screen
 * 2. Shrinks and moves precisely to DateReel's "today" dot position
 * 3. Fades out, revealing the real dot underneath
 *
 * Only plays on fresh login (justLoggedIn === true)
 *
 * @module BlueDotTransition
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuthStore } from "~/core/store/useAuthStore";

// ============================================================================
// CONFIGURATION
// ============================================================================

const PHASE_DURATION = {
  HOLD: 300,
  SHRINK: 800,
  SETTLE: 400,
};

/** Blue dot final size (matches DateReel's dot: w-1.5 h-1.5 = 6px) */
const DOT_FINAL_SIZE = 6;

/** Fallback position if DOM element not found */
const FALLBACK_POSITION = { x: 0, y: 0, found: false };

// ============================================================================
// COMPONENT
// ============================================================================

export function BlueDotTransition() {
  const justLoggedIn = useAuthStore((state) => state.justLoggedIn);
  const setJustLoggedIn = useAuthStore((state) => state.setJustLoggedIn);

  const [phase, setPhase] = useState<"idle" | "hold" | "shrink" | "settle" | "done">("idle");
  const [targetPosition, setTargetPosition] = useState<{ x: number; y: number; found: boolean }>(FALLBACK_POSITION);
  const observerRef = useRef<ResizeObserver | null>(null);

  /**
   * Find the DateReel blue dot and calculate its position dynamically
   */
  const updateTargetPosition = useCallback(() => {
    if (typeof window === "undefined") return;

    const dot = document.querySelector("[data-date-dot]");
    if (dot) {
      const rect = dot.getBoundingClientRect();
      setTargetPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
        found: true,
      });
    } else {
      // Fallback: center of screen
      setTargetPosition({
        x: window.innerWidth / 2,
        y: 70, // Approximate header position
        found: false,
      });
    }
  }, []);

  // Start animation when justLoggedIn becomes true
  useEffect(() => {
    if (!justLoggedIn) return;

    setPhase("hold");
    // Initial position calculation with slight delay for DOM to render
    const timer = setTimeout(updateTargetPosition, 50);
    return () => clearTimeout(timer);
  }, [justLoggedIn, updateTargetPosition]);

  // Phase transitions
  useEffect(() => {
    if (phase === "idle" || phase === "done") return;

    let timer: NodeJS.Timeout;

    if (phase === "hold") {
      timer = setTimeout(() => {
        updateTargetPosition(); // Update position before shrink
        setPhase("shrink");
      }, PHASE_DURATION.HOLD);
    } else if (phase === "shrink") {
      timer = setTimeout(() => setPhase("settle"), PHASE_DURATION.SHRINK);
    } else if (phase === "settle") {
      timer = setTimeout(() => {
        setPhase("done");
        setJustLoggedIn(false);
      }, PHASE_DURATION.SETTLE);
    }

    return () => clearTimeout(timer);
  }, [phase, setJustLoggedIn, updateTargetPosition]);

  // Cleanup after done
  useEffect(() => {
    if (phase === "done" && !justLoggedIn) {
      const timer = setTimeout(() => setPhase("idle"), 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [phase, justLoggedIn]);

  // Update position on window resize during animation
  useEffect(() => {
    if (phase === "idle" || phase === "done") return;

    const handleResize = () => updateTargetPosition();
    window.addEventListener("resize", handleResize);

    // Also observe the dot element for layout changes
    const dot = document.querySelector("[data-date-dot]");
    if (dot && typeof ResizeObserver !== "undefined") {
      observerRef.current = new ResizeObserver(updateTargetPosition);
      observerRef.current.observe(dot);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      observerRef.current?.disconnect();
    };
  }, [phase, updateTargetPosition]);

  if ((phase === "idle" || phase === "done") && !justLoggedIn) {
    return null;
  }

  const isExpanded = phase === "hold";
  const isSettling = phase === "settle";

  const screenSize =
    typeof window !== "undefined"
      ? Math.max(window.innerWidth, window.innerHeight) * 2.5
      : 4000;

  return (
    <AnimatePresence>
      {phase !== "done" && phase !== "idle" && (
        <motion.div
          className="fixed inset-0 z-[9999] pointer-events-none"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="absolute rounded-full bg-brand-primary"
            style={{
              boxShadow: isExpanded
                ? "0 0 150px 80px var(--status-info-bg-subtle)"
                : isSettling
                  ? "0 0 6px var(--status-info-bg-subtle)"
                  : "0 0 20px 5px var(--status-info-bg-subtle)",
            }}
            initial={{
              left: "50%",
              top: "50%",
              width: screenSize,
              height: screenSize,
              x: "-50%",
              y: "-50%",
              opacity: 1,
            }}
            animate={{
              left: isExpanded ? "50%" : targetPosition.x,
              top: isExpanded ? "50%" : targetPosition.y,
              width: isExpanded ? screenSize : DOT_FINAL_SIZE,
              height: isExpanded ? screenSize : DOT_FINAL_SIZE,
              x: isExpanded ? "-50%" : -DOT_FINAL_SIZE / 2,
              y: isExpanded ? "-50%" : -DOT_FINAL_SIZE / 2,
              opacity: isSettling ? 0 : 1,
            }}
            transition={{
              duration: 0.7,
              ease: [0.4, 0, 0.2, 1], // Smooth ease-out, no bounce
              opacity: {
                duration: 0.35,
                ease: "easeOut",
              },
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default BlueDotTransition;
